import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormHelperText,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useLocale } from "src/hooks/use-locale";
import GlobalIcon from "src/assets/svg-icons/global.svg";
import Logo from "src/assets/svg-icons/arsann-logo.svg";
import { useAddJycFreeTicketMutation } from "src/api/jyc-campiang";
import { useSetRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import PolicyPopup from "src/shared/components/policy-popup";
import { doc, increment, setDoc } from "firebase/firestore";
import { db } from "src/firebase";
import FailedTicket from "../car-wash-request/components/failed-ticket";

const APP_STORE_URL = "https://apps.apple.com/us/app/arsann/id6757977584";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.arsann.customer.app";

const redirectToAppStore = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    window.location.href = APP_STORE_URL;
    return;
  }

  if (/android/.test(userAgent)) {
    window.location.href = GOOGLE_PLAY_URL;
    return;
  }
};

interface IProps {}

const schema = Yup.object().shape({
  name: Yup.string().required("Required"),

  phone: Yup.string().required("Required"),

  // acceptPolicy: Yup.boolean().oneOf([true], "Required"),
});

interface IFormInputs extends Yup.InferType<typeof schema> {}

const JycCampaign: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocale();
  const setNotifications = useSetRecoilState(notificationsState);
  const [openPolicy, setOpenPolicy] = useState(false);

  const { mutate, isPending } = useAddJycFreeTicketMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInputs>({
    defaultValues: {
      name: "",
      phone: "",
      // acceptPolicy: false,
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const handleLanguageChange = () => {
    setLocale(locale === "en" ? "ar" : "en");
  };

  const handleOpenPolicy = () => {
    setOpenPolicy(true);
  };

  const handleClosePolicy = () => {
    setOpenPolicy(false);
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    console.log(data);

    mutate(
      {
        name: data.name,
        phoneNumber: data.phone,
      },
      {
        onSuccess: () => {
          // success logic
          setNotifications((prev) => [
            ...prev,
            {
              type: "success",
              message: t("requestedSuccessfully"),
            },
          ]);
          redirectToAppStore();
        },
        onError: (error) => {
          // error logic
          console.error("Failed to add ticket", error);
          setNotifications((prev) => [
            ...prev,
            {
              type: "error",
              message: error?.response?.data?.message ?? "Failed to submit",
            },
          ]);
        },
      },
    );
  };

  useEffect(() => {
    const incrementPageViews = async () => {
      try {
        const pageRef = doc(db, "jyc_campaign_analytics", "page_visitors");

        await setDoc(
          pageRef,
          {
            count: increment(1),
          },
          { merge: true },
        );
      } catch (error) {
        console.error("Failed to increment page views:", error);
      }
    };

    incrementPageViews();
  }, []);

  return (
    <Box borderRadius={"12px"} overflow={"hidden"}>
      <FailedTicket title={t("linkExpiredMessage")} description={" "} />
    </Box>
  );

  return (
    <Box borderRadius={"12px"} overflow={"hidden"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          padding={{
            xs: "30px 20px 100px 20px",
            md: "30px 113px 100px 113px",
          }}
          alignItems={"center"}
          minHeight={"100vh"}
        >
          {/* Language */}
          <Box width={"100%"} display={"flex"} justifyContent={"flex-start"}>
            <Button
              size="small"
              sx={{ textTransform: "unset" }}
              onClick={handleLanguageChange}
            >
              <Stack direction={"row"} gap={1} alignItems={"center"}>
                <img width={20} src={GlobalIcon} alt="" />
                <Typography
                  color={"textPrimary"}
                  fontSize={"13px"}
                  fontWeight={700}
                >
                  {locale === "en" ? "العربية" : "English"}
                </Typography>
              </Stack>
            </Button>
          </Box>

          {/* Logo */}
          <Box
            mt={5}
            mb={5}
            width={"100%"}
            display={"flex"}
            justifyContent={"center"}
          >
            <img src={Logo} alt="Arsann" width={100} />
          </Box>

          <Box maxWidth={"629px"} width={"100%"}>
            <Stack spacing={3}>
              {/* Name */}
              <Box width={"100%"}>
                <Typography fontSize={"14px"} sx={{ mb: "10px" }}>
                  {t("name")}
                </Typography>

                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      placeholder={t("name")}
                      error={!!errors.name}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#222",
                        },
                      }}
                    />
                  )}
                />

                {errors.name && (
                  <FormHelperText error>
                    {t(errors?.name?.message ?? "")}
                  </FormHelperText>
                )}
              </Box>

              {/* Phone */}
              <Box width={"100%"}>
                <Typography fontSize={"14px"} sx={{ mb: "10px" }}>
                  {t("registrationPhoneNumber")}
                </Typography>

                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      type="tel"
                      placeholder={"+9665XXXXXXXX"}
                      error={!!errors.phone}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#222",
                        },
                      }}
                    />
                  )}
                />

                {errors.phone && (
                  <FormHelperText error>
                    {t(errors?.phone?.message ?? "")}
                  </FormHelperText>
                )}
              </Box>

              {/* Usage policy */}
              {/* <Box>
                <Controller
                  name="acceptPolicy"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Typography fontSize={"14px"}>
                          {t("usagePolicy")}
                        </Typography>
                      }
                    />
                  )}
                />

                {errors.acceptPolicy && (
                  <FormHelperText error>
                    {t(errors.acceptPolicy.message ?? "")}
                  </FormHelperText>
                )}
              </Box> */}
            </Stack>

            <Stack mt={6} gap={2} alignItems={"center"}>
              <Link
                onClick={handleOpenPolicy}
                sx={{ cursor: "pointer", fontSize: "14px" }}
              >
                {t("arsannUsagePolicy")}
              </Link>
            </Stack>

            {/* Submit */}
            <LoadingButton
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              sx={{
                minWidth: { xs: "100%", sm: "310px" },
                mt: 1,
              }}
              loading={isPending}
            >
              {t("downloadAppAndRegister")}
            </LoadingButton>
          </Box>
        </Stack>
      </form>

      <PolicyPopup open={openPolicy} handleClose={handleClosePolicy} />
    </Box>
  );
};

export default JycCampaign;
